import { Alert, Link } from '@cloudscape-design/components';
import { useNavigate } from 'react-router-dom';
import { ConfigurationObject } from '../services/configs-api';
import { formatDate } from './detail/format-utils';

interface PendingDeletionWarningProps {
  config: ConfigurationObject;
  allConfigs: ConfigurationObject[];
}

/**
 * Checks if any field in the config's payload references a pending-deletion config.
 * If so, renders an amber warning banner.
 * BR-16: Existing configs that reference pending-deletion configs must show a warning.
 */
export function PendingDeletionWarning({ config, allConfigs }: PendingDeletionWarningProps) {
  const navigate = useNavigate();

  // Find all payload values that reference a pending-deletion config
  const pendingRefs: { fieldKey: string; refConfig: ConfigurationObject }[] = [];

  if (config.payload && typeof config.payload === 'object') {
    for (const [key, value] of Object.entries(config.payload)) {
      if (typeof value === 'string') {
        const referenced = allConfigs.find(
          (c) => c.configId === value && c.deletionScheduledAt
        );
        if (referenced) {
          pendingRefs.push({ fieldKey: key, refConfig: referenced });
        }
      }
    }
  }

  if (pendingRefs.length === 0) return null;

  return (
    <>
      {pendingRefs.map(({ fieldKey, refConfig }) => (
        <Alert key={fieldKey} type="warning">
          ⚠️ <strong>Warning:</strong> This configuration references{' '}
          <Link onFollow={(e) => { e.preventDefault(); navigate(`/configs/${refConfig.configId}?type=${refConfig.configType}`); }}>
            <strong>{refConfig.configName}</strong>
          </Link>
          , which has been scheduled for deletion on{' '}
          <strong>{formatDate(refConfig.deletionScheduledAt!)}</strong>.
          Please update the <strong>{fieldKey}</strong> field before then to avoid a routing failure.
        </Alert>
      ))}
    </>
  );
}
